import { useState } from 'react';
import { motion } from 'framer-motion';
import { Navigate, useParams } from 'react-router-dom';
import {
  Users,
  Settings,
  History,
  FolderOpen,
  QrCode,
  Plus,
  ArrowLeft,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/stores/authStore';
import { useWorkspaces, useMyWorkspaceRole, useWorkspaceFolders } from '@/hooks/useWorkspace';
import { TeamMembersList } from '@/components/workspace/TeamMembersList';
import { ActivityLog } from '@/components/workspace/ActivityLog';
import { CreateWorkspaceModal } from '@/components/workspace/CreateWorkspaceModal';
import { Link } from 'react-router-dom';

export default function Workspace() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { user, isLoading: authLoading } = useAuthStore();
  const { workspaces, isLoading } = useWorkspaces();
  const { data: myRole } = useMyWorkspaceRole(workspaceId);
  const { folders } = useWorkspaceFolders(workspaceId);
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);

  if (authLoading || isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // If no workspace selected, show workspace list
  if (!workspaceId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                  Workspaces
                </h1>
                <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                  Collaborate with your team
                </p>
              </div>
              <Button onClick={() => setShowCreateWorkspace(true)} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                New Workspace
              </Button>
            </div>
          </motion.div>

          {workspaces && workspaces.length > 0 ? (
            <div className="grid gap-4">
              {workspaces.map((workspace) => (
                <Link
                  key={workspace.id}
                  to={`/workspace/${workspace.id}`}
                  className="block"
                >
                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    className="p-6 rounded-lg border bg-card hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{workspace.name}</h3>
                        {workspace.description && (
                          <p className="text-sm text-muted-foreground">{workspace.description}</p>
                        )}
                      </div>
                      {workspace.ownerId === user.id && (
                        <span className="text-xs text-muted-foreground">Owner</span>
                      )}
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border rounded-lg">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No workspaces yet</h3>
              <p className="text-muted-foreground mb-4">
                Create a workspace to start collaborating with your team
              </p>
              <Button onClick={() => setShowCreateWorkspace(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Workspace
              </Button>
            </div>
          )}

          <CreateWorkspaceModal
            open={showCreateWorkspace}
            onOpenChange={setShowCreateWorkspace}
          />
        </div>
      </Layout>
    );
  }

  const workspace = workspaces?.find(w => w.id === workspaceId);

  if (!workspace) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Workspace not found</h1>
          <Button asChild>
            <Link to="/workspace">Back to Workspaces</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/workspace">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{workspace.name}</h1>
              {workspace.description && (
                <p className="text-muted-foreground">{workspace.description}</p>
              )}
            </div>
          </div>
        </motion.div>

        <Tabs defaultValue="team" className="space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-1 w-full">
            <TabsTrigger value="team" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Users className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Team</span>
            </TabsTrigger>
            <TabsTrigger value="qrcodes" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <QrCode className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">QR Codes</span>
            </TabsTrigger>
            <TabsTrigger value="folders" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <FolderOpen className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Folders</span>
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <History className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Activity</span>
            </TabsTrigger>
            {myRole === 'admin' && (
              <TabsTrigger value="settings" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">Settings</span>
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="team">
            <TeamMembersList workspaceId={workspaceId} ownerId={workspace.ownerId} />
          </TabsContent>

          <TabsContent value="qrcodes">
            <div className="text-center py-12 border rounded-lg">
              <QrCode className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Shared QR Codes</h3>
              <p className="text-muted-foreground mb-4">
                QR codes created in this workspace will appear here
              </p>
              <Button asChild>
                <Link to="/create">
                  <Plus className="h-4 w-4 mr-2" />
                  Create QR Code
                </Link>
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="folders">
            <div className="text-center py-12 border rounded-lg">
              <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Folders</h3>
              <p className="text-muted-foreground mb-4">
                Organize your QR codes with folders
              </p>
              {myRole !== 'viewer' && (
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Folder
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <ActivityLog workspaceId={workspaceId} />
          </TabsContent>

          {myRole === 'admin' && (
            <TabsContent value="settings">
              <div className="text-center py-12 border rounded-lg">
                <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Workspace Settings</h3>
                <p className="text-muted-foreground">
                  Configure workspace name, description, and preferences
                </p>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}
